import { Size, SnapActionCandidateSortAndSelectStrategies } from '@inter-ikea-core/core';
import { CoreApi, Product, ProductType } from '@inter-ikea-storageone/range';
import { StabilityComponent } from '@inter-ikea-storageone/range';
import { OutlinedComponent, PlacementResult } from '@inter-ikea-core/core';
import { stability } from '@inter-ikea-storagetwo/ivar';

export const shouldShowStabilityInfo = (coreApi: CoreApi): boolean => {
  return stability.getUnstableSections(getSectionEntities(coreApi)).length > 0;
};

export const getSectionEntities = (coreApi: CoreApi) => {
  return coreApi.entities.getByProductTypes([
    ProductType.IVAR_SECTION,
    ProductType.IVAR_CORNER_SECTION
  ]);
};

const addShelfComponents = (placementResult: PlacementResult) => {
  if (placementResult?.placedEntity) {
    placementResult.placedEntity.addComponent(new OutlinedComponent());
    placementResult.placedEntity.addComponent(new StabilityComponent());
  }
};

const findMatchingShelf = (shelves: Product[], entityComSize: Size | undefined) => {
  const possibleShelves = shelves.filter(
    shelf =>
      shelf.communicatedDimensions.width === entityComSize?.width &&
      shelf.communicatedDimensions.depth === entityComSize?.depth
  );

  //Four pack specific. Four pack should be prioritized last
  const moveFourPackShelfToEnd = (possibleShelves: Product[]) => {
    const index = possibleShelves.findIndex(item => item.type === ProductType.BROR_FOUR_PACK_SHELF);
    if (index !== -1) possibleShelves.push(...possibleShelves.splice(index, 1));
    return possibleShelves;
  };

  return moveFourPackShelfToEnd(possibleShelves)[0];
};

const addShelfToSectionConnector = (
  coreApi: CoreApi,
  shelf: Product,
  section: any, //Type otw.
  zone: any, //StabilityZone otw.
  destroy: boolean
) => {
  let placementResult: PlacementResult | null = null;
  let hole = zone.startPosition;

  while (!placementResult?.placedEntity && hole >= zone.min && hole <= zone.max) {
    placementResult = coreApi.products.addToConnector(
      shelf.coreProductId,
      section.entity,
      'to-section',
      `insert-connector-${hole}`
    );

    if (destroy && !placementResult.valid) {
      coreApi.entities.disposeCollidingEntities(placementResult.errors, false);
    }

    addShelfComponents(placementResult);
    hole = zone.climb ? hole + 2 : hole - 2;
  }
};

const addShelfToSectionTarget = (
  coreApi: CoreApi,
  shelf: Product,
  section: any, //Type otw.
  destroy: boolean
) => {
  // Need to do Math.ceil since additionalPointsRequired can be 0.5 or 1.5 and that will cause a crash when going to summary
  [...Array(Math.ceil(section.additionalPointsRequired))].forEach(() => {
    const placementResult = coreApi.products.addToTarget(
      shelf.coreProductId,
      section.entity,
      SnapActionCandidateSortAndSelectStrategies.SELECT_ALL
    );

    if (destroy) {
      coreApi.entities.disposeCollidingEntities(placementResult.errors, false);
    }

    addShelfComponents(placementResult);
  });
};

export const addShelvesToUnstableSections = (
  shelves: Product[],
  coreApi: CoreApi,
  destroy: boolean
) => {
  const sections = getSectionEntities(coreApi);
  const unstableSections = stability.getUnstableSections(sections);
  unstableSections.forEach(section => {
    //Find shelves that fits
    const shelf = findMatchingShelf(shelves, coreApi.entities.getEntityComSize(section.entity));

    if (!shelf) return;

    //Add shelves to zone start points
    section.unstableZones.forEach(zone =>
      addShelfToSectionConnector(coreApi, shelf, section, zone, destroy)
    );

    //Section requires more points to be stable
    if (section.additionalPointsRequired > 0) {
      addShelfToSectionTarget(coreApi, shelf, section, destroy);
    }
  });
  //Sections still not stable, remove non stability products in its way
  shouldShowStabilityInfo(coreApi) && addShelvesToUnstableSections(shelves, coreApi, true);
};
